import React from 'react';
import styled, { css } from 'styled-components';
import Button from 'components/ui/Button';

import { Paragraph } from 'components/ui/Typography';
import {
	spacing05x,
	spacing1x,
	spacing2x,
	spacing3x,
	spacing4x,
	spacing5x,
	spacing8x,
} from 'theme/spacings';
import {
	blue,
	blueLight,
	white,
	greyDarker,
	greyLight,
	black,
	blueDark,
	lgLightGray,
	lgBlueGray,
	lgGreen,
	lgDarkBlue,
	lgLightGrayBlue,
	beige,
} from 'theme/colors';
import { deviceSmallMax, deviceLargeMax } from 'theme/mediaQueries';
import { fontWeightLight } from 'theme/typography';

const theme = {
	blue: {
		headingColor: white,
		background: blue,
		color: white,
	},
	blueLight: {
		background: blueLight,
		headingColor: blue,
		color: black,
	},
	white: {
		headingColor: blue,
		background: white,
		color: black,
	},
	beige: {
		headingColor: blue,
		background: beige,
		color: black,
	},
	greyLight: {
		background: greyLight,
		headingColor: blue,
		color: black,
	},
	lgGreen: {
		background: lgGreen,
		headingColor: lgLightGray,
		color: lgLightGray,
	},
	lgBlueGray: {
		background: lgBlueGray,
		headingColor: lgLightGray,
		color: lgLightGray,
	},
	lgLightGrayBlue: {
		background: lgLightGrayBlue,
		headingColor: lgDarkBlue,
		color: lgDarkBlue,
	},
};

const getPatternFill = (theme) => {
	switch (theme) {
		case 'blue':
			return white;
		case 'blueLight':
			return blue;
		default:
			return greyDarker;
	}
};

export const Picture = styled.picture`
	height: 100%;
`;

export const PromoImage = styled.img`
	object-fit: cover;
	font-family: 'object-fit: cover;';
	position: absolute;
	display: block;
	left: 50%;
	bottom: 0;
	max-height: 120%;
	min-height: 110%;
	max-width: 100%;
	transform: translateX(-50%);
	@media (max-width: ${deviceLargeMax}) {
		max-height: 100%;
		min-height: 100%;
	}
	@media (max-width: ${deviceSmallMax}) {
		max-height: 100%;
		min-height: 100%;
	}
`;

export const PromoContent = styled.div`
	padding-top: ${(props) => (props.outside ? '5.1rem' : 0)};
	padding-left: ${(props) => (props.contentLeft ? 0 : spacing4x)};
	padding-right: ${(props) => (props.contentLeft ? spacing4x : 0)};

	padding-bottom: 0;

	width: 50%;
	display: ${(props) =>
		props.videoPreference === 'videoLeft' ||
		props.videoPreference === 'videoRight'
			? 'flex'
			: 'inline-block'};
	flex-direction: column;
	align-self: center;
	color: ${(props) => theme[props.theme]?.color};
	@media (max-width: ${deviceLargeMax}) {
		padding: ${(props) =>
			props.outside
				? `5.1rem 0 ${spacing2x} ${spacing2x}`
				: `${spacing2x} 0 ${spacing2x} ${spacing3x} `};
	}
	@media (max-width: 981px) {
		width: 100%;
		padding: ${spacing2x};
	}
`;
export const PromoFullWidthContent = styled.div`
	position: absolute;
	width: 58rem;
	padding: ${spacing5x};
	right: 0;
	bottom: ${spacing8x};
	background-color: ${(props) =>
		!props.hasImage && theme[props.theme]?.background};
	display: inline-block;
	@media (max-width: ${deviceLargeMax}) {
		right: ${spacing2x};
		color: inherit;
	}
	@media (max-width: ${deviceSmallMax}) {
		width: 100%;
		position: static;
		padding: ${spacing2x};
		color: ${(props) => !props.hasImage && theme[props.theme]?.color};
		background-color: ${(props) =>
			!props.hasImage && theme[props.theme]?.background};
		color: ${(props) =>
			props.theme !== 'blue' ? theme[props.theme]?.color : 'white'};
	}
`;

export const PromoBlockContent = (props) => {
	return props.fullWidth ? (
		<PromoFullWidthContent {...props} />
	) : (
		<PromoContent {...props} />
	);
};

const baseButtonStyling = css`
	margin: 0 !important;
	padding: ${spacing1x} ${spacing2x};
	display: flex;
	align-items: center;
	text-align: left;
`;

const ContentButton = styled(Button)`
	${baseButtonStyling}
	font-size: 1.6rem;
`;

const Link = styled(ContentButton)`
	${baseButtonStyling}
	font-weight: ${fontWeightLight};
	font-size: 1.6rem;
	background-color: transparent;
	border: none;
	cursor: pointer;

	color: ${({ darkBackground }) => (darkBackground ? white : blueDark)};
	:hover {
		background-color: transparent;
		color: ${({ darkBackground }) => (darkBackground ? white : blueDark)};
		text-decoration: underline;
	}
	span {
		pointer-events: none;
	}
`;

export const PromoButton = (props) => {
	return props.styling === 'button' || props.fullWidth ? (
		<ContentButton {...props} />
	) : (
		<Link {...props} />
	);
};

//Typography

export const Heading = styled.div`
	color: ${(props) => theme[props.theme]?.headingColor};
	margin: 0 0 ${spacing1x} 0;
	@media (max-width: ${deviceSmallMax}) {
		color: ${(props) => theme[props.theme]?.headingColor};
		margin: ${spacing05x} 0;
	}
`;
export const FullWidthHeading = styled(Heading)`
	color: ${(props) => theme[props.theme]?.headingColor};

	@media (max-width: ${deviceSmallMax}) {
		color: ${(props) => theme[props.theme]?.headingColor};
	}
`;

export const Text = styled(Paragraph)`
	color: ${(props) => theme[props.theme]?.color};
	font-weight: ${fontWeightLight};
	margin-top: 0;
	margin-bottom: ${spacing3x};

	@media (max-width: ${deviceSmallMax}) {
		font-size: 1.4rem;
	}
`;

export const FullWidthText = styled(Text)`
	color: ${(props) =>
		props.theme === 'blue' && !props.hasImage
			? theme['white']?.color
			: theme[props.theme]?.color};

	@media (max-width: ${deviceSmallMax}) {
		color: ${(props) => theme[props.theme]?.color};
	}
`;

export const ContentHeading = (props) => {
	return props.fullWidth ? (
		<FullWidthHeading {...props} />
	) : (
		<Heading {...props} />
	);
};

export const ContentText = (props) => {
	return props.fullWidth ? <FullWidthText {...props} /> : <Text {...props} />;
};
